import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'normalModeQuiz_state';

const defaultState = {
  currentStep: 'welcome',
  config: null,
  quizData: null,
  quizResults: null,
  currentQuizletIndex: 0,
  completedQuizlets: new Set(),
  quizletResults: {},
  mainTimer: 0,
  isTimerRunning: false
};

export const useQuizState = () => {
  const [state, setState] = useState(defaultState);

  const saveState = useCallback((newState) => {
    try {
      const stateToSave = {
        ...newState,
        // Convert Set to Array for JSON serialization
        completedQuizlets: newState.completedQuizlets instanceof Set 
          ? Array.from(newState.completedQuizlets) 
          : newState.completedQuizlets,
        timestamp: Date.now()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      // Don't call setState here - let the caller handle state updates
    } catch (error) {
      console.warn('Failed to save quiz state to localStorage:', error);
    }
  }, []);

  const loadState = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedState = JSON.parse(saved);
        // Check if state is not too old (e.g., 24 hours)
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        if (Date.now() - parsedState.timestamp < maxAge) {
          // Convert Array back to Set for completedQuizlets
          if (parsedState.completedQuizlets && Array.isArray(parsedState.completedQuizlets)) {
            parsedState.completedQuizlets = new Set(parsedState.completedQuizlets);
          }
          setState(parsedState);
          return parsedState;
        } else {
          // Clear old state
          clearState();
        }
      }
    } catch (error) {
      console.warn('Failed to load quiz state from localStorage:', error);
    }
    return null;
  }, []);

  const clearState = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setState(defaultState);
    } catch (error) {
      console.warn('Failed to clear quiz state from localStorage:', error);
    }
  }, []);

  const updateState = useCallback((updates) => {
    setState(prevState => {
      // Ensure completedQuizlets is always a Set
      const processedUpdates = { ...updates };
      if (processedUpdates.completedQuizlets && !(processedUpdates.completedQuizlets instanceof Set)) {
        processedUpdates.completedQuizlets = new Set(processedUpdates.completedQuizlets);
      }
      
      const newState = { ...prevState, ...processedUpdates };
      
      // Save to localStorage
      try {
        const stateToSave = {
          ...newState,
          completedQuizlets: newState.completedQuizlets instanceof Set 
            ? Array.from(newState.completedQuizlets) 
            : newState.completedQuizlets,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      } catch (error) {
        console.warn('Failed to save quiz state to localStorage:', error);
      }
      
      return newState;
    });
  }, []);

  const resetQuiz = useCallback(() => {
    clearState();
  }, [clearState]);

  const resumeQuiz = useCallback(() => {
    const savedState = loadState();
    return savedState;
  }, [loadState]);

  // Auto-save state changes (only when meaningful state changes occur)
  useEffect(() => {
    if (state.currentStep !== 'welcome') {
      // Only save if this is a meaningful state change, not just a re-render
      const stateToSave = {
        ...state,
        completedQuizlets: state.completedQuizlets instanceof Set 
          ? Array.from(state.completedQuizlets) 
          : state.completedQuizlets,
        timestamp: Date.now()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    }
  }, [state.currentStep, state.currentQuizletIndex, state.mainTimer, state.isTimerRunning]);

  return {
    state,
    updateState,
    saveState,
    loadState,
    clearState,
    resetQuiz,
    resumeQuiz
  };
};
